import { has } from 'lodash'
import { createApp, defineAsyncComponent } from 'vue'

const TEMPLATE_NAMES = {
  LICENSE_EXPIRED: 'licence_expired',
  NOT_AUTHORIZED: 'not_authorized_jira',
  USER_NOT_ADDED: 'user-not-added',
  ACCESS_TOKEN_NOT_ADDED: 'access-token-not-added'
}

const LicenseExpiredApp = defineAsyncComponent(() => import('@jira/LicenseExpiredApp'))
const NotAuthorizedApp = defineAsyncComponent(() => import('@jira/NotAuthorizedApp'))

const JIRA_APP_FALLBACK_APPS = {
  [TEMPLATE_NAMES.LICENSE_EXPIRED]: LicenseExpiredApp,
  [TEMPLATE_NAMES.NOT_AUTHORIZED]: NotAuthorizedApp
}

const JIRA_WIDGET_FALLBACK_APPS = {
  [TEMPLATE_NAMES.NOT_AUTHORIZED]: NotAuthorizedApp
}

const JIRA_GADGET_FALLBACK_APPS = {
  [TEMPLATE_NAMES.LICENSE_EXPIRED]: LicenseExpiredApp,
  [TEMPLATE_NAMES.NOT_AUTHORIZED]: NotAuthorizedApp
}

const UserNotAddedApp = defineAsyncComponent(() => import('@jira/UserNotAddedApp'))
const AccessTokenNotAddedApp = defineAsyncComponent(() => import('@jira/AccessTokenNotAddedApp'))

const CONFLUENCE_MACRO_FALLBACK_APPS = {
  [TEMPLATE_NAMES.USER_NOT_ADDED]: UserNotAddedApp,
  [TEMPLATE_NAMES.ACCESS_TOKEN_NOT_ADDED]: AccessTokenNotAddedApp
}

const CONFLUENCE_MACRO_SETTINGS_FALLBACK_APPS = {
  [TEMPLATE_NAMES.USER_NOT_ADDED]: UserNotAddedApp,
  [TEMPLATE_NAMES.ACCESS_TOKEN_NOT_ADDED]: AccessTokenNotAddedApp
}

/**
 * Attempts to render a fallback template from the given map.
 *
 * If a fallback template exists for the given name, mounts it on the provided
 * root element and returns the Vue app instance. Returns null otherwise,
 * which can be used as a signal to proceed with the standard initialization flow.
 *
 * @param {Record<string, import('vue').Component>} templatesMap - Fallback templates map to look up.
 * @param {string | null | undefined} templateName - The name of the fallback template to look up.
 * @param {HTMLElement} rootElement - The DOM element to mount the fallback app on.
 * @returns {import('vue').App | null} The mounted Vue app instance, or null if no fallback was rendered.
 */
const tryMountFallbackApp = (templatesMap = {}, templateName, rootElement) => {
  if (templateName && templatesMap && has(templatesMap, templateName)) {
    const app = createApp(templatesMap[templateName])

    app.mount(rootElement)
    return app
  }

  return null
}

/**
 * @param {string | null | undefined} templateName
 * @param {HTMLElement} rootElement
 */
export const tryMountFallbackAppForJira = (templateName, rootElement) =>
  tryMountFallbackApp(JIRA_APP_FALLBACK_APPS, templateName, rootElement)

/**
 * @param {string | null | undefined} templateName
 * @param {HTMLElement} rootElement
 */
export const tryMountFallbackAppForWidget = (templateName, rootElement) =>
  tryMountFallbackApp(JIRA_WIDGET_FALLBACK_APPS, templateName, rootElement)

/**
 * @param {string | null | undefined} templateName
 * @param {HTMLElement} rootElement
 */
export const tryMountFallbackAppForGadget = (templateName, rootElement) =>
  tryMountFallbackApp(JIRA_GADGET_FALLBACK_APPS, templateName, rootElement)

/**
 * @param {string | null | undefined} templateName
 * @param {HTMLElement} rootElement
 */
export const tryMountFallbackAppForConfluenceMacro = (templateName, rootElement) =>
  tryMountFallbackApp(CONFLUENCE_MACRO_FALLBACK_APPS, templateName, rootElement)

/**
 * @param {string | null | undefined} templateName
 * @param {HTMLElement} rootElement
 */
export const tryMountFallbackAppForConfluenceMacroSettings = (templateName, rootElement) =>
  tryMountFallbackApp(CONFLUENCE_MACRO_SETTINGS_FALLBACK_APPS, templateName, rootElement)
